import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import gradeService from '../../services/gradeService';
import schoolService from '../../services/schoolService';
import classService from '../../services/classService';
import semesterService from '../../services/semesterService';
import { getGradeDescription, getGradeColor } from '../../utils/gradeColors';
import LoadingSpinner from '../common/LoadingSpinner';

const formatDate = (iso) => {
  if (!iso) return 'Dates not set';
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

function StudentManagement() {
const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selection state
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);
  
  // Student data
  const [students, setStudents] = useState([]);
  const [studentGrades, setStudentGrades] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentAcademicYear] = useState('2024/2025');

  useEffect(() => {
    // Only show this component for Admin and Berufsbildner
    if (!user?.roles?.some(role => ['ADMIN', 'BERUFSBILDNER'].includes(role.name))) {
      setError('Access denied. This feature is only available for administrators and instructors.');
      setLoading(false);
      return;
    }
    
    initializeData();
  }, [user]);



  const initializeData = async () => {
    try {
      setLoading(true);
      const schoolsData = await schoolService.getAllSchools();
      setSchools(schoolsData);
      
      if (schoolsData.length > 0) {
        setSelectedSchool(schoolsData[0]);
      } else {
        setError('No schools found. Please add schools first.');
      }
    } catch (error) {
      setError(`Error loading initial data: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = useCallback(async () => {
    if (!selectedSchool) return;
    try {
      const classesData = await classService.getClassesBySchool(selectedSchool.id);
      setClasses(classesData);
      
      if (classesData.length > 0) {
        setSelectedClass(classesData[0]);
      } else {
        setError('No classes found for the selected school.');
      }
    } catch (error) {
      console.error('❌ Error loading classes:', error);
      setError(`Error loading classes: ${error.message || error}`);
    }
  }, [selectedSchool]);

  const loadSemesters = useCallback(async () => {
    if (!selectedSchool || !selectedClass) return;
    try {
      const semestersData = await semesterService.getSemestersByClass(selectedSchool.id, selectedClass.id);
      setSemesters(semestersData);
      
      if (semestersData.length > 0) {
        setSelectedSemester(semestersData[0]);
      } else {
        setError('No semesters found for the selected class.');
      }
    } catch (error) {
      setError(`Error loading semesters: ${error.message || error}`);
    }
  }, [selectedSchool, selectedClass]);

  const loadStudentProfiles = useCallback(async () => {
    if (!selectedClass || !selectedSemester) return;
    try {
      setLoading(true);
      
      // Get students from the selected class
      const studentsData = await classService.getStudentsByClass(selectedClass.id);
      setStudents(studentsData);

      if (studentsData.length === 0) {
        setError('No students found in the selected class.');
        return;
      }

      // Load grades for each student
      const gradesPromises = studentsData.map(async (student) => {
        try {
          const grades = await gradeService.getGradesByUserAndSemester(
            student.id, 
            selectedSemester.name || selectedSemester.id?.toString(),
            currentAcademicYear
          );
          return { studentId: student.id, grades };
        } catch (error) {
          console.error(`❌ Error loading grades for student ${student.id}:`, error);
          return { studentId: student.id, grades: [] };
        }
      });

      const gradesResults = await Promise.all(gradesPromises);
      const gradesMap = {};
      gradesResults.forEach(result => {
        gradesMap[result.studentId] = result.grades;
      });
      
      setStudentGrades(gradesMap);

    } catch (error) {
      console.error('Error loading apprentice profiles:', error);
      setError('Error loading apprentice profiles');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSemester, currentAcademicYear]);

  useEffect(() => {
    if (selectedSchool) {
      loadClasses();
    }
  }, [selectedSchool, loadClasses]);

  useEffect(() => {
    if (selectedSchool && selectedClass) {
      loadSemesters();
    }
  }, [selectedSchool, selectedClass, loadSemesters]);

  useEffect(() => {
    if (selectedSchool && selectedClass && selectedSemester) {
      loadStudentProfiles();
    }
  }, [selectedSchool, selectedClass, selectedSemester, loadStudentProfiles]);

  const calculateStudentAverage = (grades) => {
    if (!grades || grades.length === 0) return null;
    const sum = grades.reduce((acc, grade) => acc + parseFloat(grade.grade), 0);
    return (sum / grades.length).toFixed(2);
  };

  const filteredStudents = students.filter(student =>
    student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && schools.length === 0) {
    return <LoadingSpinner message="Loading apprentice profiles..." fullScreen={false} />;
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-1 text-primary">
                <i className="fas fa-user-graduate me-2"></i>
                Apprentice Profiles
              </h1>
              <p className="text-muted mb-0">
                View detailed apprentice information and academic performance
              </p>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Selection Controls */}
          <div className="card border-primary mb-4">
            <div className="card-body">
              <h5 className="card-title text-primary">
                <i className="fas fa-filter me-2"></i>
                Select School, Class and Semester
              </h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">School</label>
                  <select 
                    className="form-select"
                    onChange={(e) => setSelectedSchool(schools.find(s => s.id === parseInt(e.target.value)))}
                    value={selectedSchool?.id || ""}
                  >
                    <option value="" disabled>Select a school...</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Class</label>
                  <select 
                    className="form-select"
                    onChange={(e) => setSelectedClass(classes.find(c => c.id === parseInt(e.target.value)))}
                    value={selectedClass?.id || ""}
                    disabled={!selectedSchool}
                  >
                    <option value="" disabled>
                      {!selectedSchool ? 'Select a school first' : 'Select a class...'}
                    </option>
                    {classes.map(clazz => (
                      <option key={clazz.id} value={clazz.id}>{clazz.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Semester</label>
                  <select 
                    className="form-select"
                    onChange={(e) => setSelectedSemester(semesters.find(s => s.id === parseInt(e.target.value)))}
                    value={selectedSemester?.id || ""}
                    disabled={!selectedClass}
                  >
                    <option value="" disabled>
                      {!selectedClass ? 'Select a class first' : 'Select a semester...'}
                    </option>
                    {semesters.map(semester => (
                      <option key={semester.id} value={semester.id}>{semester.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          {students.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <label className="form-label">
                      <i className="fas fa-search me-2"></i>Search Students
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setSearchTerm('')}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className="bg-light p-3 rounded">
                      <div className="text-primary fw-bold">
                        {searchTerm ? filteredStudents.length : students.length}
                      </div>
                      <small className="text-muted">
                        {searchTerm ? `of ${students.length} students` : 'students total'}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Information */}
          {selectedSchool && selectedClass && selectedSemester && students.length > 0 && (
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-info-circle me-3 text-info"></i>
                <div>
                  <strong>System Status:</strong> All data is loading correctly! 
                  Students are displayed with profile information. 
                  {Object.values(studentGrades).every(grades => grades.length === 0) && (
                    <span> No grades have been entered yet - this is normal for a new system.</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Apprentice Profiles */}
          {selectedSchool && selectedClass && selectedSemester && (
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-users me-2"></i>
                      Apprentice Profiles - {selectedSchool.name} - {selectedClass.name} - {selectedSemester.name}
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    {filteredStudents.length === 0 ? (
                      <div className="text-center py-5 px-4">
                        <div className="mb-4">
                          <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center" 
                               style={{ width: '100px', height: '100px' }}>
                            <i className="fas fa-user-graduate text-muted" style={{ fontSize: '3rem' }}></i>
                          </div>
                        </div>
                        <h4 className="text-muted mb-3">
                          {searchTerm ? 'No Matching Students Found' : 'No Students Found'}
                        </h4>
                        <p className="text-muted mb-4">
                          {searchTerm 
                            ? 'Try adjusting your search terms or clearing the search to see all students.' 
                            : 'No students found for the selected class and semester.'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Student</th>
                              <th>Contact</th>
                              <th>Apprenticeship</th>
                              <th className="text-center">Grades</th>
                              <th className="text-center">Average</th>
                              <th className="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredStudents.map((student) => {
                              const grades = studentGrades[student.id] || [];
                              const average = calculateStudentAverage(grades);
                              
                              return (
                                <tr key={student.id}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="bg-primary rounded-circle p-2 me-3" style={{ width: '40px', height: '40px' }}>
                                        <i className="fas fa-user text-white"></i>
                                      </div>
                                      
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <div><i className="fas fa-envelope me-1"></i> {student.firstName} {student.lastName}</div>
                                      <small className="text-muted">
                                        <i className="fas fa-user me-1"></i> {student.username}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <div>
                                        <i className="fas fa-briefcase me-1"></i> 
                                        {student.professionType || 'Not specified'}
                                      </div>
                                      <small className="text-muted">
                                        {student.apprenticeshipStartDate && student.apprenticeshipEndDate ? (
                                          <>
                                            <i className="fas fa-calendar me-1"></i>
                                            {formatDate(student.apprenticeshipStartDate)} - {formatDate(student.apprenticeshipEndDate)}
                                          </>
                                        ) : (
                                          'Dates not set'
                                        )}
                                      </small>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                      {grades.length > 0 ? (
                                        grades.slice(0, 5).map((grade, index) => (
                                          <span 
                                            key={index}
                                            className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gray-100"
                                            style={{ color: getGradeColor(grade.grade) }}
                                            title={`${grade.subjectName}: ${grade.grade} (${getGradeDescription(grade.grade)})`}
                                          >
                                            {parseFloat(grade.grade).toFixed(1)}
                                          </span>
                                        ))
                                      ) : (
                                        <div className="text-center">
                                          <span className="text-muted">
                                            <i className="fas fa-chart-line me-1"></i>
                                            No grades yet
                                          </span>
                                          <br />
                                          <small className="text-success">
                                            <i className="fas fa-check-circle me-1"></i>
                                            Ready for grading
                                          </small>
                                        </div>
                                      )}
                                      {grades.length > 5 && (
                                        <span className="badge bg-secondary">+{grades.length - 5}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    {average ? (
                                      <div>
                                        <div 
                                          className="fw-bold"
                                          style={{ color: getGradeColor(average) }}
                                        >
                                          {average}
                                        </div>
                                        <div className="progress mt-1" style={{ height: '6px' }}>
                                          <div 
                                            className="progress-bar"
                                            style={{ 
                                              width: `${(parseFloat(average) / 6) * 100}%`,
                                              backgroundColor: getGradeColor(average)
                                            }}
                                          ></div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-center">
                                        <span className="text-muted">No average</span>
                                        <br />
                                        <small className="text-info">
                                          <i className="fas fa-info-circle me-1"></i>
                                          Awaiting grades
                                        </small>
                                      </div>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {average && (
                                      <span 
                                        className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gray-100"
                                        style={{ color: getGradeColor(average) }}
                                      >
                                        {parseFloat(average) >= 4.0 ? 'Pass' : 'At Risk'}
                                      </span>
                                    )}
                                    <div className="mt-1">
                                      <small className="text-muted">
                                        {grades.length} grade{grades.length !== 1 ? 's' : ''}
                                      </small>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentManagement;